import { Controller } from "@hotwired/stimulus"

// Live recalculation for the PO review table. As the user edits a
// quantity or unit_price cell, the row's line total updates and the
// grand total in the table footer follows. No backend round-trip.
//
// Wiring (in review_purchase_order.html.erb):
//   - form (or table) carries data-controller="po-totals"
//   - each <tr> in the body carries data-po-totals-target="row"
//   - the qty input has data-totals-qty
//   - the price input has data-totals-price
//   - the line-total cell has data-totals-line
//   - the grand-total cell carries data-po-totals-target="grandTotal"
//   - input events bubble up via data-action="input->po-totals#recalculate"
//
// We use plain data attributes (data-totals-qty / -price / -line) on the
// row's inputs/cells rather than Stimulus targets because there are
// many rows; this keeps the DOM lighter and the controller simpler.
export default class extends Controller {
    static targets = ["row", "subtotal", "tax", "grandTotal", "taxRate"]

    connect() {
        this.recalculate()
        // If a customer is already selected (pre-filled from the PO), apply
        // their price-list prices straight away.
        if (this.customerSelect()?.value) this.customerChanged()
    }

    // Called when the user picks a different pricing tier. For every
    // row that has a catalog_prices map (set on the row's
    // data-totals-prices attribute), overwrite the unit_price input
    // with the new tier's price. Rows without a price for the chosen
    // tier are left alone (graceful fallback for products that only
    // have a Retail tier, for example).
    tierChanged(event) {
        const tier = event.target.value
        if (!tier) return

        this.rowTargets.forEach((row) => {
            const pricesJson = row.dataset.totalsPrices
            if (!pricesJson) return

            let prices
            try { prices = JSON.parse(pricesJson) }
            catch (e) { return }

            const newPrice = prices[tier]
            if (newPrice == null) return

            const priceInput = row.querySelector("[data-totals-price]")
            if (priceInput) priceInput.value = parseFloat(newPrice).toFixed(2)
        })

        this.recalculate()
    }

    customerSelect() {
        return this.element.querySelector("[name='customer_id']")
    }

    // Re-price every matched line through the selected customer's price list.
    // A line whose product the list prices is switched to that list price (and
    // flagged "from <list>"); lines the list doesn't cover keep their catalog
    // tier price. Fires when the customer dropdown changes (and on connect).
    async customerChanged() {
        const customerId = this.customerSelect()?.value
        const ids = []
        this.rowTargets.forEach((row) => {
            const sel = row.querySelector("[name$='[product_id]']")
            if (sel && sel.value) ids.push(sel.value)
        })
        if (!customerId || ids.length === 0) { this.clearListSource(); return }

        const params = new URLSearchParams()
        params.set("customer_id", customerId)
        ids.forEach((id) => params.append("product_ids[]", id))

        let data
        try {
            const resp = await fetch(`/orders/po_customer_prices?${params.toString()}`, { headers: { Accept: "application/json" } })
            if (!resp.ok) return
            data = await resp.json()
        } catch (e) { return }

        const prices = (data && data.prices) || {}
        this.rowTargets.forEach((row) => {
            const sel    = row.querySelector("[name$='[product_id]']")
            const pid    = sel && sel.value
            const lp     = pid != null ? prices[pid] : null
            const listEl = row.querySelector("[data-totals-source]")
            const catEl  = row.querySelector("[data-totals-catalog-source]")
            if (lp == null) {
                if (listEl) listEl.hidden = true
                if (catEl) catEl.hidden = false
                return
            }
            const priceInput = row.querySelector("[data-totals-price]")
            if (priceInput) priceInput.value = parseFloat(lp).toFixed(2)
            if (listEl) { listEl.textContent = `from ${data.list_name || "price list"}`; listEl.hidden = false }
            if (catEl) catEl.hidden = true
        })
        this.recalculate()
    }

    clearListSource() {
        this.rowTargets.forEach((row) => {
            const listEl = row.querySelector("[data-totals-source]")
            const catEl  = row.querySelector("[data-totals-catalog-source]")
            if (listEl) listEl.hidden = true
            if (catEl) catEl.hidden = false
        })
    }

    recalculate() {
        // Sum the line totals across all visible (non-skipped) rows.
        let subtotal = 0
        this.rowTargets.forEach((row) => {
            // Skip rows that the user marked as "skip" (the parent po-row
            // controller dims them; we shouldn't sum their dollars).
            if (row.style.opacity && parseFloat(row.style.opacity) < 0.5) return

            const qtyEl   = row.querySelector("[data-totals-qty]")
            const priceEl = row.querySelector("[data-totals-price]")
            const lineEl  = row.querySelector("[data-totals-line]")

            const qty   = parseFloat(qtyEl?.value)   || 0
            const price = parseFloat(priceEl?.value) || 0
            const total = qty * price

            if (lineEl) lineEl.textContent = `$${total.toFixed(2)}`
            subtotal += total
        })

        // Tax is stored / submitted as a PERCENTAGE (e.g. 8.5 = 8.5%),
        // matching how the Order model expects it on save. Read via
        // a Stimulus target (not an arbitrary data attribute) so the
        // wiring is enforced by Stimulus and we don't silently fall
        // back to 0 when something is misnamed.
        const taxRate = this.hasTaxRateTarget ? (parseFloat(this.taxRateTarget.value) || 0) : 0
        const tax     = subtotal * (taxRate / 100)
        const grand   = subtotal + tax

        if (this.hasSubtotalTarget)   this.subtotalTarget.textContent   = `$${subtotal.toFixed(2)}`
        if (this.hasTaxTarget)        this.taxTarget.textContent        = `$${tax.toFixed(2)}`
        if (this.hasGrandTotalTarget) this.grandTotalTarget.textContent = `$${grand.toFixed(2)}`
    }
}
